import React, { useState } from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Search, BookOpen } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';

export const LexiquePage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('Tous');

  const lexiqueTerms = [
    // Matériel et équipement
    { term: 'Hausse', definition: 'Élément de la ruche placé au-dessus du corps, destiné à recevoir les cadres de miel que les abeilles stockent en surplus.', category: 'Matériel' },
    { term: 'Corps de ruche', definition: 'Partie principale de la ruche où vit la colonie. Contient les cadres de couvain et les réserves de la reine.', category: 'Matériel' },
    { term: 'Cadre', definition: 'Structure rectangulaire en bois qui soutient la cire gaufrée et que les abeilles construisent en alvéoles.', category: 'Matériel' },
    { term: 'Enfumoir', definition: 'Outil de l\'apiculteur produisant de la fumée pour calmer les abeilles lors des visites de ruche.', category: 'Matériel' },
    { term: 'Lève-cadre', definition: 'Outil métallique servant à décoller et extraire les cadres de la ruche.', category: 'Matériel' },
    { term: 'Extracteur', definition: 'Machine permettant d\'extraire le miel des cadres par force centrifuge sans détruire les alvéoles.', category: 'Matériel' },
    { term: 'Cire gaufrée', definition: 'Feuille de cire avec alvéoles préformées que l\'on fixe sur les cadres pour faciliter le travail des abeilles.', category: 'Matériel' },
    
    // Biologie et vie de la colonie
    { term: 'Essaimage', definition: 'Phénomène naturel de reproduction de la colonie : une partie des abeilles quitte la ruche avec l\'ancienne reine pour fonder une nouvelle colonie.', category: 'Biologie' },
    { term: 'Couvain', definition: 'Ensemble des œufs, larves et nymphes d\'abeilles présents dans la ruche. Indicateur de la santé de la colonie.', category: 'Biologie' },
    { term: 'Reine', definition: 'Seule femelle fertile de la colonie, elle pond jusqu\'à 2000 œufs par jour. Elle vit 3-5 ans.', category: 'Biologie' },
    { term: 'Faux-bourdon', definition: 'Abeille mâle issue d\'un œuf non fécondé. Son unique rôle est de féconder les reines lors du vol nuptial.', category: 'Biologie' },
    { term: 'Ouvrière', definition: 'Abeille femelle non fertile qui accomplit toutes les tâches de la colonie selon son âge : nettoyage, nourrissage, construction, butinage.', category: 'Biologie' },
    { term: 'Phéromones', definition: 'Substances chimiques sécrétées par les abeilles pour communiquer au sein de la colonie.', category: 'Biologie' },
    { term: 'Orpheline', definition: 'Se dit d\'une colonie qui a perdu sa reine et qui doit en élever une nouvelle.', category: 'Biologie' },
    
    // Maladies et parasites
    { term: 'Varroa', definition: 'Acarien parasite des abeilles, principale menace sanitaire. Se reproduit dans le couvain et affaiblit les abeilles adultes.', category: 'Santé' },
    { term: 'Loque', definition: 'Maladie bactérienne du couvain, très contagieuse. La loque américaine est à déclaration obligatoire.', category: 'Santé' },
    { term: 'Nosémose', definition: 'Maladie digestive causée par un champignon microscopique affectant les abeilles adultes.', category: 'Santé' },
    { term: 'Frelon asiatique', definition: 'Prédateur invasif des abeilles, originaire d\'Asie. Peut décimer des colonies entières.', category: 'Santé' },
    
    // Production et récolte
    { term: 'Miellée', definition: 'Période pendant laquelle les abeilles récoltent du nectar en abondance et produisent du miel.', category: 'Production' },
    { term: 'Nectar', definition: 'Liquide sucré sécrété par les fleurs, récolté par les abeilles et transformé en miel.', category: 'Production' },
    { term: 'Pollen', definition: 'Poussière fécondante des fleurs, riche en protéines, récoltée par les abeilles pour nourrir les larves.', category: 'Production' },
    { term: 'Propolis', definition: 'Résine végétale récoltée par les abeilles, utilisée pour colmater la ruche et la désinfecter grâce à ses propriétés antibactériennes.', category: 'Production' },
    { term: 'Gelée royale', definition: 'Substance nutritive produite par les jeunes abeilles pour nourrir les larves et la reine.', category: 'Production' },
    { term: 'Opercule', definition: 'Fine pellicule de cire avec laquelle les abeilles scellent les alvéoles de miel mûr ou de couvain.', category: 'Production' },
    { term: 'Désoperculer', definition: 'Retirer les opercules des cadres de miel avant l\'extraction.', category: 'Production' },
    
    // Techniques apicoles
    { term: 'Transhumance', definition: 'Déplacement des ruches vers différents lieux de floraison pour optimiser la production de miel.', category: 'Technique' },
    { term: 'Nourrissement', definition: 'Apport de sirop de sucre aux abeilles lorsque les réserves naturelles sont insuffisantes, notamment avant l\'hiver.', category: 'Technique' },
    { term: 'Hivernage', definition: 'Période hivernale où la colonie ralentit son activité et forme une grappe pour maintenir la chaleur.', category: 'Technique' },
    { term: 'Division', definition: 'Technique consistant à séparer une colonie en deux pour créer une nouvelle ruche.', category: 'Technique' },
    { term: 'Grille à reine', definition: 'Grille placée entre le corps et la hausse pour empêcher la reine de monter pondre dans les hausses à miel.', category: 'Technique' },
    
    // Législation
    { term: 'NAPI', definition: 'Numéro d\'Apiculteur : identifiant unique obligatoire à afficher sur chaque ruche, obtenu après déclaration.', category: 'Législation' },
    { term: 'DDPP', definition: 'Direction Départementale de la Protection des Populations : organisme auprès duquel déclarer ses ruches.', category: 'Législation' },
    { term: 'Déclaration de rucher', definition: 'Obligation légale de déclarer toutes ses ruches chaque année entre septembre et décembre.', category: 'Législation' },
  ];

  const categories = ['Tous', ...new Set(lexiqueTerms.map(item => item.category))];

  const filteredTerms = lexiqueTerms
    .filter(item => {
      const matchesSearch = item.term.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            item.definition.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = selectedCategory === 'Tous' || item.category === selectedCategory;
      return matchesSearch && matchesCategory;
    })
    .sort((a, b) => a.term.localeCompare(b.term));

  return (
    <div className="min-h-screen bg-cream">
      <Header />
      
      <main>
        {/* Hero Section with Background Image */}
        <section className="relative py-24 overflow-hidden">
          <div className="absolute inset-0">
            <img 
              src="https://images.unsplash.com/photo-1473973266408-ed4e27abdd47" 
              alt="Lexique apiculture"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-black/50"></div>
          </div>
          
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold text-white mb-4">
                Lexique de l'Apiculture
              </h1>
              <p className="text-lg text-white/90">
                Découvrez tous les termes techniques pour comprendre le monde des abeilles et de l'apiculture.
              </p>
            </div>
          </div>
        </section>

        {/* Search and Filters */}
        <section className="py-8 bg-white border-b sticky top-16 z-40">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto">
              {/* Search Bar */}
              <div className="relative mb-6">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                <Input 
                  type="text"
                  placeholder="Rechercher un terme (ex: hausse, varroa, essaimage...)"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 py-6 text-lg border-2 focus:border-honey"
                />
              </div>

              {/* Category Filters */}
              <div className="flex flex-wrap gap-3">
                {categories.map(category => (
                  <button
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                    className={`px-4 py-2 rounded-full font-medium transition-all ${
                      selectedCategory === category
                        ? 'bg-honey text-white shadow-md'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {category}
                  </button>
                ))}
              </div>

              {/* Results count */}
              <div className="mt-4 text-sm text-gray-600">
                {filteredTerms.length} terme{filteredTerms.length > 1 ? 's' : ''} trouvé{filteredTerms.length > 1 ? 's' : ''}
              </div>
            </div>
          </div>
        </section>

        {/* Terms List */}
        <section className="py-16">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto">
              {filteredTerms.length > 0 ? (
                <div className="grid md:grid-cols-2 gap-6">
                  {filteredTerms.map((item, index) => (
                    <Card 
                      key={index} 
                      className="border-2 border-gray-200 hover:border-honey transition-all hover:shadow-lg"
                    >
                      <CardContent className="p-6">
                        <div className="flex items-start justify-between mb-3">
                          <h3 className="text-xl font-bold text-gray-900">
                            {item.term}
                          </h3>
                          <Badge className="bg-sage text-white">
                            {item.category}
                          </Badge>
                        </div>
                        <p className="text-gray-700 leading-relaxed">
                          {item.definition}
                        </p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="text-center py-16">
                  <p className="text-xl text-gray-600">Aucun terme trouvé pour votre recherche.</p>
                  <button 
                    onClick={() => {
                      setSearchTerm('');
                      setSelectedCategory('Tous');
                    }}
                    className="mt-4 text-honey hover:underline"
                  >
                    Réinitialiser la recherche
                  </button>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Info Box */}
        <section className="py-12 bg-honey/10">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto text-center">
              <div className="bg-white rounded-xl shadow-lg p-8 border-2 border-honey/20">
                <h3 className="text-2xl font-serif font-bold text-gray-900 mb-4">
                  💡 Vous ne trouvez pas un terme ?
                </h3>
                <p className="text-gray-700 mb-4">
                  Ce lexique est en constante évolution. N'hésitez pas à nous suggérer de nouveaux termes à ajouter pour aider la communauté des apiculteurs débutants.
                </p>
                <p className="text-sm text-gray-600">
                  Les définitions sont volontairement simplifiées pour être accessibles aux débutants.
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};
